import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Swal from 'sweetalert2';
import { useModal } from '../../hooks/useModal';
import { referenceService, tenderService, quotationService, companyService } from '../../services/api';
import { cleanText } from '../../utils/textHelpers';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const formatDisplayValue = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  const parts = value.toString().split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return parts.join('.');
};

const TenderManagement = ({ user }) => {
  // Check permission: Admin or Manager can edit
  const canEdit = ['Administrator', 'Manager'].includes(user?.role);
  // Complete tender stages matching the flow chart
  const tenderStages = [
    { id: 1, name: 'Draft Tender', color: 'secondary' },
    { id: 2, name: 'Not Participated', color: 'dark' },
    { id: 3, name: 'Submitted Tender', color: 'info' },
    { id: 4, name: 'Under Technical Evaluation', color: 'warning' },
    { id: 5, name: 'Technically Qualified', color: 'success' },
    { id: 6, name: 'Technically Disqualified', color: 'danger' },
    { id: 7, name: 'Financial Bid Opened', color: 'info' },
    { id: 8, name: 'Lowest', color: 'success' },
    { id: 9, name: 'Awarded Tender (Pending Delivery)', color: 'success' }, // Matches User Stage 4
    { id: 10, name: 'Submitted Performance Security', color: 'primary' },
    { id: 11, name: 'Delivered Tender (Pending Payment)', color: 'primary' }, // Matches User Stage 5
    { id: 12, name: 'Completed Tender (Pending Performance Security)', color: 'warning' }, // Matches User Stage 6
    { id: 13, name: 'Release Performance Security', color: 'info' },
    { id: 14, name: 'Closed Tender', color: 'success' }, // Performance security released - Matches User Stage 9
    { id: 15, name: 'Lost (Technically Disqualified)', color: 'danger' }, // Matches User Stage 7
    { id: 16, name: 'Not Lowest', color: 'danger' },
    { id: 17, name: 'Lost (Technically Disqualified w/ Grievance)', color: 'danger' },
    { id: 18, name: 'Grievance Filled', color: 'warning' },
    { id: 19, name: 'Grievance Accepted', color: 'success' },
    { id: 20, name: 'Grievance Rejected', color: 'danger' },
    { id: 21, name: 'Release Earnest Money', color: 'info' },
    { id: 22, name: 'Cancelled', color: 'dark' }, // Matches User Stage 8
    { id: 23, name: 'Lost (Not Lowest)', color: 'danger' },
  ];

  const [tenders, setTenders] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [company, setCompany] = useState({});

  // Fetch tenders, organizations, and projects from Backend
  useEffect(() => {
    loadTenders();
    loadOrganizations();
    loadProjects();
    loadCompany();
  }, []);

  const handleViewTender = async (tender) => {
    try {
      setLoading(true);
      const res = await tenderService.getById(tender.id);
      setSelectedTender(res.data);
      setShowViewModal(true);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching tender details:", error);
      Swal.fire('Error', 'Failed to fetch tender details.', 'error');
      setLoading(false);
    }
  };

  const handleViewQuotation = async (q) => {
    try {
      setLoading(true);
      const res = await quotationService.getById(q.id);
      setSelectedQuotationForView(res.data);
      setShowViewQuotationModal(true);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching quotation details:", error);
      Swal.fire('Error', 'Failed to fetch quotation details.', 'error');
      setLoading(false);
    }
  };

  const generatePDF = async (quotation, type = 'standard') => {
    try {
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      const pageWidth = doc.internal.pageSize.width;
      let yPos = 15;

      // Logo
      if (company.logo && company.logo.startsWith('data:image')) {
        try {
          const format = company.logo.includes('image/jpeg') ? 'JPEG' : 'PNG';
          doc.addImage(company.logo, format, 15, yPos, 40, 20);
        } catch (err) { console.error("Logo error:", err); }
      }

      // Distributor Logos
      let activeLogos = [];
      try {
        const pref = quotation.distributorLogo || 'default';
        let potentialLogos = [];
        if (company.distributorLogos) {
          const parsed = JSON.parse(company.distributorLogos);
          if (Array.isArray(parsed)) potentialLogos = parsed;
        }
        if (potentialLogos.length === 0 && company.distributorLogo) {
          potentialLogos.push({ src: company.distributorLogo, active: true });
        }
        if (pref === 'none') {
          activeLogos = [];
        } else if (pref === 'default' || !pref) {
          activeLogos = potentialLogos.filter(l => l.active);
        } else if (pref.startsWith('logo_')) {
          const index = parseInt(pref.replace('logo_', ''));
          if (potentialLogos[index]) activeLogos = [potentialLogos[index]];
        }
      } catch (e) { console.error("Error parsing distributorLogos", e); }

      if (activeLogos.length > 0) {
        const logoWidth = 35;
        const logoHeight = 15;
        const spacing = 2;
        const rightMargin = pageWidth - 15;
        activeLogos.reverse().forEach((logo, index) => {
          if (logo.src && logo.src.startsWith('data:image')) {
            try {
              const format = logo.src.includes('image/jpeg') ? 'JPEG' : 'PNG';
              const xPos = rightMargin - ((index + 1) * logoWidth) - (index * spacing);
              doc.addImage(logo.src, format, xPos, yPos, logoWidth, logoHeight);
            } catch (err) { console.error("Logo render error", err); }
          }
        });
      }

      doc.setFontSize(18);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(44, 62, 80);
      doc.text(company.name || 'Company Name', pageWidth / 2, yPos + 8, { align: 'center' });

      doc.setFontSize(9);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(100);
      const contactInfo = company.address || '';
      const contactLine = [
        company.phone ? `Tel: ${company.phone}` : '',
        company.email ? `Email: ${company.email}` : '',
        company.website ? `Web: ${company.website}` : ''
      ].filter(Boolean).join(' | ');

      let textY = yPos + 14;
      doc.text(contactInfo, pageWidth / 2, textY, { align: 'center' });
      textY += 5;
      doc.text(contactLine, pageWidth / 2, textY, { align: 'center' });
      textY += 5;

      doc.setDrawColor(200);
      doc.line(15, textY + 5, pageWidth - 15, textY + 5);

      yPos = textY + 15;
      doc.setFontSize(11);
      doc.setTextColor(44, 62, 80);
      doc.setFont(undefined, 'bold');
      doc.text('To:', 15, yPos);

      doc.setFontSize(10);
      doc.setTextColor(0);
      doc.setFont(undefined, 'bold');
      doc.text(quotation.toContact || 'Contact Person', 15, yPos + 6);
      doc.setFont(undefined, 'normal');
      doc.text(quotation.designation || '', 15, yPos + 11);
      doc.text(quotation.organization || 'Client Organization', 15, yPos + 16);

      const rightX = pageWidth - 60;
      doc.setFont(undefined, 'bold');
      doc.text(`Quotation No:`, rightX, yPos);
      doc.setFont(undefined, 'normal');
      doc.text(quotation.quoteNo || '-', rightX + 25, yPos, { align: 'left' });

      doc.setFont(undefined, 'bold');
      doc.text(`Date:`, rightX, yPos + 6);
      doc.setFont(undefined, 'normal');
      doc.text(quotation.submissionDate || '-', rightX + 25, yPos + 6, { align: 'left' });

      if (quotation.validityDate) {
        doc.setFont(undefined, 'bold');
        doc.text(`Valid Until:`, rightX, yPos + 12);
        doc.setFont(undefined, 'normal');
        doc.text(quotation.validityDate, rightX + 25, yPos + 12, { align: 'left' });
      }

      doc.setFont(undefined, 'bold');
      doc.setFontSize(11);
      doc.text(`Subject: ${quotation.subject || 'Quotation'}`, 15, yPos + 30);

      let tableColumn = ["S.No", "Product", "Model", "Description", "Qty", "Unit Price", "Total"];
      if (type === 'technical') tableColumn = ["S.No", "Product", "Model", "Description", "Qty"];

      const tableRows = [];
      const qItems = quotation.QuotationItems || [];
      qItems.forEach((item, index) => {
        // Sanitize description text
        const cleanDesc = (item.description || '-').replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim();

        let row = [
          index + 1,
          item.productName || item.description || '-',
          item.modelNumber || '-',
          { content: cleanDesc, styles: { halign: 'left', font: 'helvetica', charSpace: 0, overflow: 'linebreak' } },
          item.quantity || 0,
          parseFloat(item.unitPrice || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
          parseFloat(item.totalPrice || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
        ];
        if (type === 'technical') row = row.slice(0, 5);
        tableRows.push(row);
      });

      if (type !== 'technical') {
        tableRows.push([
          { content: 'Sub Total', colSpan: 6, styles: { halign: 'right', fontStyle: 'bold' } },
          { content: `Rs. ${parseFloat(quotation.subTotal || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`, styles: { halign: 'right' } }
        ]);
        if (quotation.gstAmount > 0) {
          tableRows.push([
            { content: `GST (${quotation.gstRate || 0}%)`, colSpan: 6, styles: { halign: 'right', fontStyle: 'bold' } },
            { content: `Rs. ${parseFloat(quotation.gstAmount || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`, styles: { halign: 'right' } }
          ]);
        }
        if (quotation.installationCharges > 0) {
          tableRows.push([
            { content: 'Installation/Comm:', colSpan: 6, styles: { halign: 'right', fontStyle: 'bold' } },
            { content: `Rs. ${parseFloat(quotation.installationCharges || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`, styles: { halign: 'right' } }
          ]);
        }
        tableRows.push([
          { content: 'Grand Total', colSpan: 6, styles: { halign: 'right', fontStyle: 'bold', fillColor: [255, 255, 0] } },
          { content: `Rs. ${parseFloat(quotation.grandTotal || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`, styles: { halign: 'right', fontStyle: 'bold', fillColor: [255, 255, 0] } }
        ]);
      }

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: yPos + 35,
        theme: 'grid',
        headStyles: { fillColor: [44, 62, 80], textColor: 255, fontSize: 10, halign: 'center' },
        styles: { font: 'helvetica', fontSize: 9, cellPadding: 3, valign: 'middle', halign: 'left', charSpace: 0 },
        bodyStyles: { halign: 'left' },
        columnStyles: type === 'technical' ? { 0: { halign: 'center', cellWidth: 15 }, 3: { halign: 'left', overflow: 'linebreak' }, 4: { halign: 'center' } } : { 0: { halign: 'center', cellWidth: 15 }, 3: { halign: 'left', overflow: 'linebreak' }, 4: { halign: 'center' }, 5: { halign: 'right' }, 6: { halign: 'right' } },
        alternateRowStyles: { fillColor: [245, 245, 245] }
      });

      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text('Thank you for your business.', pageWidth / 2, doc.internal.pageSize.height - 10, { align: 'center' });
        doc.text(`Page ${i} of ${pageCount}`, pageWidth - 15, doc.internal.pageSize.height - 10, { align: 'right' });
      }

      doc.addPage();
      doc.setFont(undefined, 'bold');
      doc.setFontSize(12);
      doc.setTextColor(0);
      doc.text('TERMS & CONDITIONS', 15, 20);
      doc.setFont(undefined, 'normal');
      doc.setFontSize(9);

      const terms = (quotation.termsAndConditions || '').split('\n');
      let termY = 30;
      terms.forEach(term => {
        const splitText = doc.splitTextToSize(term, pageWidth - 30);
        doc.text(splitText, 15, termY);
        termY += (splitText.length * 4) + 2;
      });

      termY += 10;
      doc.text("Looking forward for your esteemed purchase order.", 15, termY);
      termY += 15;
      doc.text("Sincerely yours", 15, termY);

      let userSignature = '';
      try {
        const storedUser = JSON.parse(localStorage.getItem('rsms_user') || '{}');
        if (storedUser.id) {
          const uRes = await authService.getUser(storedUser.id);
          if (uRes.data && uRes.data.signature) userSignature = uRes.data.signature;
        }
      } catch (e) { console.error('Error fetching user signature for PDF', e); }

      const user = JSON.parse(localStorage.getItem('rsms_user') || '{}');
      const userName = user.name || user.username || 'Authorized Signatory';
      termY += 40;

      if (userSignature && userSignature.startsWith('data:image')) {
        try {
          const sigFormat = userSignature.includes('image/jpeg') ? 'JPEG' : 'PNG';
          doc.addImage(userSignature, sigFormat, 15, termY - 25, 40, 20);
        } catch (e) { }
      }

      doc.setFont(undefined, 'bold');
      doc.text(userName, 15, termY);
      doc.setFont(undefined, 'normal');
      doc.text(`For ${company.name || 'Company Name'}`, 15, termY + 5);

      const filenamePrefix = type === 'technical' ? 'Technical Quotation' : 'Quotation';
      doc.save(`${filenamePrefix} - ${quotation.quoteNo || 'draft'}.pdf`);
    } catch (error) {
      console.error("PDF Generation Error:", error);
      Swal.fire('Error', 'Failed to generate PDF.', 'error');
    }
  };

  const loadTenders = async () => {
    try {
      const response = await tenderService.getAll();
      setTenders(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading tenders:', error);
      Swal.fire('Error', 'Failed to load tenders from server', 'error');
      setLoading(false);
    }
  };

  const loadOrganizations = async () => {
    try {
      const response = await referenceService.getOrgs();
      const sortedOrgs = response.data.sort((a, b) => a.name.localeCompare(b.name));
      setOrganizations(sortedOrgs);
    } catch (error) {
      console.error("Error loading organizations:", error);
    }
  };

  const loadProjects = async () => {
    try {
      const response = await referenceService.getProjects(); // Assuming getProjects exists in referenceService or needs to be added
      setProjects(response.data);
    } catch (error) {
      console.error("Error loading projects:", error);
    }
  };

  const loadCompany = async () => {
    try {
      const response = await companyService.getBranding();
      setCompany(response.data);
    } catch (error) {
      console.error("Error loading company branding:", error);
    }
  };

  const [showModal, setShowModal] = useState(false);
  const [selectedTender, setSelectedTender] = useState(null);
  const [showStageModal, setShowStageModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showPSModal, setShowPSModal] = useState(false);
  const [showEMModal, setShowEMModal] = useState(false);
  const [showOrgModal, setShowOrgModal] = useState(false); // New: Org Creation Modal
  const [selectedQuotationForView, setSelectedQuotationForView] = useState(null);
  const [showViewQuotationModal, setShowViewQuotationModal] = useState(false);
  const [newOrgData, setNewOrgData] = useState({
    name: '',
    abbreviation: '',
    ntn: '',
    city: '',
    country: '',
    address: '',
    contactPerson: '',
    designation: '',
    email: '',
    phone: '',
    location: ''
  });

  // Hide navbar when any modal is open
  useModal(showModal || showStageModal || showViewModal || showPSModal || showEMModal, () => {
    setShowModal(false);
    setShowStageModal(false);
    setShowViewModal(false);
    setShowPSModal(false);
    setShowEMModal(false);
  });
  const [psAmount, setPsAmount] = useState('');
  const [emReleaseDate, setEmReleaseDate] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    tenderNo: '',
    organization: '',
    project: '',
    submissionDate: '',
    value: '',
    earnestMoneyAmount: '',
    performanceSecurityAmount: '',
    currentStage: 1,
    stageName: 'Draft Tender',
    documents: []
  });
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [errors, setErrors] = useState({});

  const handleTenderNoChange = (e) => {
    const value = e.target.value;
    const isDuplicate = tenders.some(t =>
      t.tenderNo &&
      t.tenderNo.toLowerCase() === value.toLowerCase() &&
      (selectedTender ? t.id !== selectedTender.id : true)
    );

    setErrors(prev => ({
      ...prev,
      tenderNo: isDuplicate ? 'Tender No already exists!' : null
    }));

    setFormData({ ...formData, tenderNo: value });
  };

  const handleAmountChange = (e, field) => {
    const value = e.target.value.replace(/,/g, '');
    if (/^\d*\.?\d*$/.test(value)) {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleStageChange = async (tenderId, newStage) => {
    const tender = tenders.find(t => t.id === tenderId);

    // Intercept "Submitted Performance Security" (10) to force modal
    if (newStage === 10) {
      setSelectedTender(tender);
      setShowStageModal(false);
      setShowPSModal(true);
      return;
    }

    // Intercept "Submitted Tender" (3) to enforce Earnest Money
    if (newStage === 3 && !tender.earnestMoneyAmount) {
      setShowStageModal(false);
      Swal.fire({
        title: 'Earnest Money Required',
        text: 'Please enter the Earnest Money Amount to submit this tender:',
        input: 'number',
        inputAttributes: { min: 0, step: 0.01 },
        showCancelButton: true,
        confirmButtonText: 'Submit & Update Stage',
        showLoaderOnConfirm: true,
        preConfirm: (amount) => {
          if (!amount || parseFloat(amount) <= 0) {
            Swal.showValidationMessage('Please enter a valid amount');
          }
          return amount;
        }
      }).then(async (result) => {
        if (result.isConfirmed) {
          const amount = parseFloat(result.value);
          try {
            const stageName = tenderStages.find(s => s.id === newStage).name;
            const payload = {
              currentStage: newStage,
              stageName: stageName,
              earnestMoneyAmount: amount,
              previousStage: tender.currentStage // Save previous stage
            };
            await tenderService.update(tenderId, payload);
            updateLocalTenders(tenderId, payload);
            Swal.fire('Success', 'Tender submitted with Earnest Money!', 'success');
          } catch (error) {
            console.error('Error updating stage with EM:', error);
            Swal.fire('Error', 'Failed to update tender', 'error');
          }
        }
      });
      return;
    }

    // Explicitly handle "Cancelled" (22)
    if (newStage === 22) {
      try {
        const payload = {
          currentStage: 22,
          stageName: `Cancelled (from ${tender.stageName})`,
          previousStage: tender.currentStage
        };
        await tenderService.update(tenderId, payload);
        updateLocalTenders(tenderId, payload);
        setShowStageModal(false);
        Swal.fire('Success', 'Tender Cancelled successfully!', 'success');
      } catch (error) {
        console.error('Error cancelling tender:', error);
        Swal.fire('Error', 'Failed to cancel tender', 'error');
      }
      return;
    }

    // Intercept "Completed Tender" (12)
    if (newStage === 12) {
      if (tender.earnestMoneyAmount && !tender.earnestMoneyReleased) {
        setShowStageModal(false);
        Swal.fire({
          title: 'Release Earnest Money Required',
          text: 'Earnest Money must be released before ensuring completion. Please select the release date:',
          input: 'date',
          showCancelButton: true,
          confirmButtonText: 'Release & Complete',
          showLoaderOnConfirm: true,
          preConfirm: (date) => {
            if (!date) {
              Swal.showValidationMessage('Please select a release date');
            }
            return date;
          }
        }).then(async (result) => {
          if (result.isConfirmed) {
            try {
              const stageName = tenderStages.find(s => s.id === newStage).name;
              const payload = {
                currentStage: newStage,
                stageName: stageName,
                earnestMoneyReleased: true,
                earnestMoneyReleaseDate: result.value,
                previousStage: tender.currentStage
              };
              await tenderService.update(tenderId, payload);
              updateLocalTenders(tenderId, payload);
              Swal.fire('Success', 'Tender completed and Earnest Money released!', 'success');
            } catch (error) {
              console.error('Error updating stage with EM release:', error);
              Swal.fire('Error', 'Failed to update tender', 'error');
            }
          }
        });
        return;
      }
    }

    try {
      const stageName = tenderStages.find(s => s.id === newStage).name;
      const payload = {
        currentStage: newStage,
        stageName: stageName,
        previousStage: tender.currentStage // Always save previous
      };

      await tenderService.update(tenderId, payload);
      updateLocalTenders(tenderId, payload);
      setShowStageModal(false);
      Swal.fire('Success', 'Tender stage updated successfully!', 'success');
    } catch (error) {
      console.error('Error updating stage:', error);
      setShowStageModal(false);
      Swal.fire('Error', 'Failed to update tender stage', 'error');
    }
  };

  const updateLocalTenders = (tenderId, payload) => {
    const updatedTenders = tenders.map(t => {
      if (t.id === tenderId) {
        const updated = { ...t, ...payload };
        // Also update selectedTender if it matches
        if (selectedTender && selectedTender.id === tenderId) {
          setSelectedTender(updated);
        }
        return updated;
      }
      return t;
    });
    setTenders(updatedTenders);
  };

  const getStageBadge = (stageId) => {
    const stage = tenderStages.find(s => s.id === stageId);
    return `bg-${stage ? stage.color : 'secondary'}`;
  };

  const getNextStages = (currentStage, tender) => {
    // STRICT FLOWCHART MAPPING
    const stageFlow = {
      1: [3, 2, 22],                // Draft -> Submitted, Not Participated, Cancelled
      3: [4],                       // Submitted -> Under Tech Eval (Cancelled removed per text/image)
      4: [5, 6, 22],                // Under Tech -> Qualified, Disqualified, Cancelled (Grievance removed per image)

      6: [21, 18],                  // Disqualified -> Release EM, Grievance

      5: [7, 18],                   // Qualified -> Financial, Grievance

      // Grievance Routes (18) - Context Aware
      18: [19, 20],                 // Grievance Filled -> Accepted, Rejected

      19: [7],                      // Grievance Accepted -> Financial Bid Opened

      20: [7],                      // Grievance Rejected -> Financial Bid Opened

      // Release EM Routes (21) - Context Aware for "Lost" final state
      21: (() => {
        // If previous was Disqualified (6) -> Lost (Technically Disqualified) (15)
        if (tender?.previousStage === 6) return [15, 22];
        // If previous was Grievance Rejected (20) -> Lost (Technically Disqualified w/ Grievance) (17)
        if (tender?.previousStage === 20) return [17, 22];
        // If previous was Not Lowest (16) -> Lost (Not Lowest) (23)
        if (tender?.previousStage === 16) return [23];
        // If previous was Submitted PS (10) -> Delivered (11)
        if (tender?.previousStage === 10) return [11];
        // If previous was Delivered (11) -> Completed (12)
        if (tender?.previousStage === 11) return [12];
        // Note: Logic for 7->16 below. If we are at 21, and history implies 7/16, go to 16.
        return [15, 16, 17, 22]; // Fallback to all if history is lost/unknown
      })(),

      7: [8, 16, 22],               // Financial Opened -> Lowest, Not Lowest, Cancelled

      16: [21],                     // Not Lowest -> Release EM
      23: [],                       // Lost (Not Lowest) -> Final

      8: [9, 22],                   // Lowest -> Awarded, Cancelled

      9: [10],                      // Awarded -> Submitted PS (Strict: No shortcut to 11)

      10: [11, 21],                 // Submitted PS -> Delivered, Release EM

      11: [12],                     // Delivered -> Completed

      12: [13],                     // Completed -> Release PS

      13: [14],                     // Release PS -> Closed

      // Final States (Empty)
      2: [], 22: [], 14: [], 15: [], 17: []
    };

    // Grievance (18) and Release EM (21) use function logic, others array
    const result = stageFlow[currentStage];
    if (typeof result === 'function') return result();
    return result || [];
  };

  const documentTypes = [
    'Tender Documents Original',
    'Tender Notice',
    'Newspaper Cutting *',
    'Corrigendum Notice*',
    'Corrigendum Tender Documents*',
    'Newspaper Cutting Corrigendum*',
    'Request Letter*',
    'Technical Quotation PDF',
    'Technical Quotation Excel',
    'Financial Quotation PDF',
    'Financial Quotation Excel',
    'Technical Bid Final Uploaded',
    'Financial Bid Final Uploaded',
    'Earnest Money Draft',
    'OEM BOQ',
    'OEM Email chain',
    'Design Report*',
    'Technical Evaluation Report',
    'Financial Evaluation Report',
    'Grievance Letter*',
    'Vendor Quotations*',
    'Other'
  ];

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const fileList = files.map((file, index) => ({
      id: Date.now() + index,
      name: file.name,
      size: file.size,
      type: file.type,
      file: file,
      docType: '', // Default empty, user selects
      customDocType: '',
      uploadDate: new Date().toISOString().split('T')[0]
    }));
    setUploadedFiles([...uploadedFiles, ...fileList]);
  };

  const handleDocTypeChange = (fileId, type) => {
    setUploadedFiles(uploadedFiles.map(f =>
      f.id === fileId ? { ...f, docType: type, customDocType: type === 'Other' ? f.customDocType : '' } : f
    ));
  };

  const handleCustomDocTypeChange = (fileId, value) => {
    setUploadedFiles(uploadedFiles.map(f =>
      f.id === fileId ? { ...f, customDocType: value } : f
    ));
  };

  const removeFile = (fileId) => {
    setUploadedFiles(uploadedFiles.filter(f => f.id !== fileId));
  };

  const handlePerformanceSecuritySubmit = async () => {
    if (!psAmount || parseFloat(psAmount) <= 0) {
      Swal.fire('Error', 'Please enter a valid Performance Security amount!', 'error');
      return;
    }

    try {
      const payload = {
        performanceSecurityAmount: parseFloat(psAmount),
        performanceSecuritySubmitted: true,
        performanceSecuritySubmittedDate: new Date().toISOString().split('T')[0],
        currentStage: 10,
        stageName: 'Submitted Performance Security'
      };

      await tenderService.update(selectedTender.id, payload);

      const updatedTenders = tenders.map(tender => {
        if (tender.id === selectedTender.id) {
          return {
            ...tender,
            ...payload
          };
        }
        return tender;
      });
      setTenders(updatedTenders);
      setShowPSModal(false);
      setPsAmount('');
      setSelectedTender(null);
      Swal.fire('Success', 'Performance Security submitted successfully!', 'success');
    } catch (error) {
      console.error('Error submitting security:', error);
      setShowPSModal(false); // Close on error
      Swal.fire('Error', 'Failed to submit Performance Security', 'error');
    }
  };

  const handleEarnestMoneyRelease = async () => {
    if (!emReleaseDate) {
      Swal.fire('Error', 'Please select a release date!', 'error');
      return;
    }

    try {
      const payload = {
        earnestMoneyReleased: true,
        earnestMoneyReleaseDate: emReleaseDate
      };

      await tenderService.update(selectedTender.id, payload);

      const updatedTenders = tenders.map(tender => {
        if (tender.id === selectedTender.id) {
          return {
            ...tender,
            ...payload
          };
        }
        return tender;
      });
      setTenders(updatedTenders);
      setShowEMModal(false);
      setEmReleaseDate('');
      setSelectedTender(null);
      Swal.fire('Success', 'Earnest Money release recorded successfully!', 'success');
    } catch (error) {
      console.error('Error releasing earnest money:', error);
      setShowEMModal(false); // Close on error
      Swal.fire('Error', 'Failed to record Earnest Money release', 'error');
    }
  };

  const handleCreateOrg = async () => {
    if (!newOrgData.name.trim()) {
      Swal.fire('Error', 'Organization Name is required', 'error');
      return;
    }
    try {
      await referenceService.createOrg(newOrgData);
      await loadOrganizations(); // Reload list
      setFormData({ ...formData, organization: newOrgData.name }); // Auto-select
      setNewOrgData({
        name: '', abbreviation: '', ntn: '', city: '', country: '',
        address: '', contactPerson: '', designation: '', email: '', phone: '', location: ''
      });
      setShowOrgModal(false);
      Swal.fire('Success', 'Organization added successfully', 'success');
    } catch (error) {
      console.error(error);
      const msg = error.response?.data?.error || 'Failed to add organization';
      Swal.fire('Error', msg, 'error');
    }
  };


  const uploadNewFiles = async () => {
    const newFiles = uploadedFiles.filter(f => f.file); // Filter objects that have the File object
    if (newFiles.length === 0) return [];

    const formData = new FormData();
    newFiles.forEach(f => {
      formData.append('files', f.file);
    });

    const response = await tenderService.uploadFiles(formData);
    return response.data;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate earnest money if submitting
    if (formData.currentStage === 3 && !formData.earnestMoneyAmount) {
      Swal.fire('Error', 'Earnest Money Amount is required when submitting a tender!', 'error');
      return;
    }

    try {
      // 1. Upload new files if any
      let newDocs = [];
      const newFilesToUpload = uploadedFiles.filter(f => f.file);
      if (newFilesToUpload.length > 0) {
        const uploadedDocs = await uploadNewFiles();
        // Merge uploaded docs with local state to get docType
        newDocs = uploadedDocs.map((doc, index) => ({
          ...doc,
          docType: newFilesToUpload[index].docType,
          customDocType: newFilesToUpload[index].customDocType
        }));
      }

      // 2. Prepare documents list (existing + new)
      // For create, it's mostly new, but we handle it generically
      const existingDocs = uploadedFiles.filter(f => !f.file);
      const finalDocuments = [...existingDocs, ...newDocs];

      // Validate that all documents have a type selected
      if (finalDocuments.some(d => !d.docType)) {
        Swal.fire('Error', 'Please select a Document Type for all files.', 'error');
        return;
      }

      // Prepare payload for API
      const payload = {
        tenderNo: formData.tenderNo, // Manual Input
        ...formData,
        value: parseFloat(formData.value) || 0,
        earnestMoneyAmount: formData.currentStage === 3 ? parseFloat(formData.earnestMoneyAmount) || 0 : null,
        performanceSecurityAmount: formData.performanceSecurityAmount ? parseFloat(formData.performanceSecurityAmount) : null,
        currentStage: formData.currentStage === 3 ? 4 : formData.currentStage, // Auto-advance if submitted
        stageName: formData.currentStage === 3 ? 'Under Technical Evaluation' : formData.stageName,
        status: 'active',
        documents: finalDocuments, // Include real document paths
        OrganizationId: organizations.find(o => o.name === formData.organization)?.id || null // Link Organization Entity
      };

      await tenderService.create(payload);
      Swal.fire('Success', 'Tender created successfully!', 'success');
      setShowModal(false);
      loadTenders(); // Reload list from DB

      // Reset form
      setFormData({
        title: '',
        organization: '',
        project: '',
        submissionDate: '',
        value: '',
        earnestMoneyAmount: '',
        performanceSecurityAmount: '',
        currentStage: 1,
        stageName: 'Draft Tender',
        documents: []
      });
      setUploadedFiles([]);
    } catch (error) {
      console.error('Error creating tender:', error);

      let errorMessage = 'Failed to create tender';
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (typeof error.response?.data === 'string') {
        errorMessage = error.response.data;
      } else if (error.message) {
        errorMessage = error.message;
      }

      Swal.fire('Error', errorMessage, 'error');
      // Modal stays open
    }
  };

  const handleDeleteTender = async (id) => {
    try {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!'
      });

      if (result.isConfirmed) {
        await tenderService.delete(id);
        Swal.fire('Deleted!', 'Tender has been deleted.', 'success');
        loadTenders();
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Cannot Delete Tender',
        text: error.response?.data?.error || 'Failed to delete tender.',
      });
    }
  };

  const handleEditTender = (tender) => {
    setSelectedTender(tender);

    // Ensure existing documents have IDs so they can be individually removed
    const docsWithIds = (tender.documents || []).map((doc, index) => ({
      ...doc,
      id: doc.id || `existing-${Date.now()}-${index}`
    }));

    setFormData({
      title: tender.title,
      tenderNo: tender.tenderNo || '',
      organization: tender.organization,
      project: tender.ProjectId || (tender.Project ? tender.Project.id : ''),
      submissionDate: tender.submissionDate,
      value: tender.value,
      earnestMoneyAmount: tender.earnestMoneyAmount || '',
      performanceSecurityAmount: tender.performanceSecurityAmount || '',
      currentStage: tender.currentStage,
      stageName: tender.stageName,
      documents: docsWithIds
    });
    setUploadedFiles(docsWithIds); // Re-hydrate uploadedFiles for display
    setErrors({});
    setShowModal(true);
  };

  const handleSaveTender = async (e) => {
    e.preventDefault();
    if (selectedTender && showModal) {
      // Update Logic
      try {
        let newDocs = [];
        const newFilesToUpload = uploadedFiles.filter(f => f.file);
        if (newFilesToUpload.length > 0) {
          const uploadedDocs = await uploadNewFiles();
          // Merge uploaded docs with local state to get docType
          newDocs = uploadedDocs.map((doc, index) => ({
            ...doc,
            docType: newFilesToUpload[index].docType,
            customDocType: newFilesToUpload[index].customDocType
          }));
        }

        const existingDocs = uploadedFiles.filter(f => !f.file);
        const finalDocuments = [...existingDocs, ...newDocs];

        // Validate that all documents have a type selected
        if (finalDocuments.some(d => !d.docType)) {
          Swal.fire('Error', 'Please select a Document Type for all files.', 'error');
          return;
        }

        const payload = {
          ...formData,
          value: parseFloat(formData.value) || 0,
          earnestMoneyAmount: formData.earnestMoneyAmount ? parseFloat(formData.earnestMoneyAmount) : null,
          performanceSecurityAmount: formData.performanceSecurityAmount ? parseFloat(formData.performanceSecurityAmount) : null,
          documents: finalDocuments, // Include uploaded files
        };
        await tenderService.update(selectedTender.id, payload);
        Swal.fire('Success', 'Tender updated successfully!', 'success');
        setShowModal(false);
        loadTenders();
        setSelectedTender(null);
        setFormData({
          title: '', organization: '', submissionDate: '', value: '', earnestMoneyAmount: '', performanceSecurityAmount: '', currentStage: 1, stageName: 'Draft Tender', documents: []
        });
      } catch (error) {
        console.error('Update Error:', error);

        let errorMessage = 'Failed to update tender';
        if (error.response?.data?.error) {
          errorMessage = error.response.data.error;
        } else if (typeof error.response?.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.message) {
          errorMessage = error.message;
        }

        Swal.fire('Error', errorMessage, 'error');
        // Modal stays open
      }
    } else {
      // Create Logic (existing handleSubmit)
      handleSubmit(e);
    }
  };


  const filteredTenders = tenders.filter(tender =>
    !searchTerm ||
    tender.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tender.tenderNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (tender.organization && tender.organization.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (tender.Project && tender.Project.title && tender.Project.title.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div>
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <h2 className="mb-1" style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: '700',
            fontSize: '2rem'
          }}>
            Tender Management
          </h2>
          <p className="text-muted mb-0">Track and manage all tender processes</p>
        </div>

        {canEdit && (
          <button className="btn btn-primary" onClick={() => {
            setErrors({});
            setShowModal(true);
          }}>
            <i className="bi bi-plus-circle"></i> Create New Tender
          </button>
        )}
      </div>

      <div className="row g-3 mb-4">
        <div className="col-6 col-md-3">
          <div className="card text-center">
            <div className="card-body">
              <h3 className="text-primary">{tenders.filter(t => t.currentStage === 1).length}</h3>
              <p className="mb-0">Draft</p>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card text-center">
            <div className="card-body">
              <h3 className="text-info">{tenders.filter(t => t.currentStage === 3).length}</h3>
              <p className="mb-0">Submitted</p>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card text-center">
            <div className="card-body">
              <h3 className="text-warning">{tenders.filter(t => t.currentStage === 4).length}</h3>
              <p className="mb-0">Under Evaluation</p>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card text-center">
            <div className="card-body">
              <h3 className="text-success">{tenders.filter(t => t.currentStage === 9).length}</h3>
              <p className="mb-0">Awarded</p>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card text-center">
            <div className="card-body">
              <h3 className="text-danger">
                {tenders.filter(t => [15, 16, 17].includes(t.currentStage)).length}
              </h3>
              <p className="mb-0">Lost</p>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card text-center">
            <div className="card-body">
              <h3 style={{ color: '#8B4513' }}>{tenders.filter(t => t.currentStage === 11).length}</h3>
              <p className="mb-0">Delivered</p>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card text-center">
            <div className="card-body">
              <h3 style={{ color: 'var(--secondary-color)' }}>{tenders.filter(t => t.currentStage === 12).length}</h3>
              <p className="mb-0">Completed</p>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card text-center">
            <div className="card-body">
              <h3 style={{ color: 'var(--gray-600)' }}>{tenders.filter(t => t.currentStage === 14).length}</h3>
              <p className="mb-0">Closed</p>
            </div>
          </div>
        </div>


      </div>

      <div className="card">
        <div className="card-header">
          <h5 className="mb-0">All Tenders</h5>
        </div>
        <div className="card-body">
          <div className="mb-3">
            <input
              type="text"
              className="form-control"
              placeholder="Search tenders by Title, No, Organization or Project..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Tender No.</th>
                  <th>Title</th>
                  <th>Organization</th>

                  <th>Current Stage</th>
                  <th>Submission Date</th>
                  <th>Documents</th>
                  <th>Value</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTenders.length === 0 ? (
                  <tr><td colSpan="8" className="text-center">No tenders found</td></tr>
                ) : (
                  filteredTenders.map((tender) => (
                    <tr key={tender.id}>
                      <td><strong>{tender.tenderNo}</strong></td>
                      <td>{tender.title}</td>
                      <td>{tender.organization}</td>

                      <td>
                        <span className={`badge ${getStageBadge(tender.currentStage)}`}>
                          {tender.currentStage === 22 ? tender.stageName : (tenderStages.find(s => s.id === tender.currentStage)?.name || tender.stageName)}
                        </span>
                      </td>
                      <td>{tender.submissionDate}</td>
                      <td>
                        {tender.documents && tender.documents.length > 0 ? (
                          <span className="badge bg-secondary">
                            <i className="bi bi-paperclip me-1"></i>
                            {tender.documents.length}
                          </span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>Rs. {formatDisplayValue(tender.value)}</td>
                      <td>
                        {canEdit && (
                          <>
                            <button
                              className="btn btn-sm btn-outline-primary me-2"
                              onClick={() => {
                                setSelectedTender(tender);
                                setShowStageModal(true);
                              }}
                              title="Update Stage"
                            >
                              <i className="bi bi-arrow-right-circle"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-outline-warning me-2"
                              onClick={() => handleEditTender(tender)}
                              title="Edit Tender"
                            >
                              <i className="bi bi-pencil"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger me-2"
                              onClick={() => handleDeleteTender(tender.id)}
                              title="Delete Tender"
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </>
                        )}
                        <button
                          className="btn btn-sm btn-outline-info"
                          onClick={() => handleViewTender(tender)}
                          title="View Details"
                        >
                          <i className="bi bi-eye"></i>
                        </button>
                      </td>
                    </tr>
                  )))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {
        showStageModal && selectedTender && (
          <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Update Tender Stage</h5>
                  <button type="button" className="btn-close" onClick={() => setShowStageModal(false)}><i className="bi bi-x-lg"></i></button>
                </div>
                <div className="modal-body">
                  <p><strong>Current Stage:</strong> {selectedTender.stageName}</p>
                  <p><strong>Next Possible Stages:</strong></p>
                  <div className="list-group">
                    {getNextStages(selectedTender.currentStage, selectedTender).map((stageId) => {
                      const stage = tenderStages.find(s => s.id === stageId);
                      return (
                        <button
                          key={stageId}
                          className="list-group-item list-group-item-action"
                          onClick={() => handleStageChange(selectedTender.id, stageId)}
                        >
                          {stage.name}
                        </button>
                      );
                    })}

                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowStageModal(false)}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {
        showModal && (
          <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{selectedTender ? 'Edit Tender' : 'Create New Tender'}</h5>
                  <button type="button" className="btn-close" onClick={() => { setShowModal(false); setSelectedTender(null); }}><i className="bi bi-x-lg"></i></button>
                </div>
                <form onSubmit={handleSaveTender}>
                  <div className="modal-body">
                    <div className="mb-3">
                      <label className="form-label">Tender No. *</label>
                      <input
                        type="text"
                        className={`form-control ${errors.tenderNo ? 'is-invalid' : ''}`}
                        value={formData.tenderNo}
                        onChange={handleTenderNoChange}
                        required
                        placeholder="Enter Official Tender Number"
                      />
                      {errors.tenderNo && <div className="invalid-feedback">{errors.tenderNo}</div>}
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Title *</label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          value={formData.title}
                          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                          required
                          placeholder="Enter tender title"
                        />
                        <button
                          className="btn btn-outline-secondary"
                          type="button"
                          onClick={() => setFormData({ ...formData, title: cleanText(formData.title) })}
                          title="Cleanup PDF artifacts"
                        >
                          <i className="bi bi-magic"></i>
                        </button>
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Organization *</label>
                      <div className="input-group">
                        <select
                          className="form-select"
                          value={formData.organization}
                          onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                          required
                        >
                          <option value="">Select Organization</option>
                          {organizations.map(org => (
                            <option key={org.id} value={org.name}>{org.name}</option>
                          ))}
                        </select>
                        <button
                          className="btn btn-outline-secondary"
                          type="button"
                          onClick={() => setShowOrgModal(true)}
                          title="Add New Organization"
                        >
                          <i className="bi bi-plus-lg"></i>
                        </button>
                      </div>
                      {organizations.length === 0 && <small className="text-muted">No organizations found. Please add them.</small>}
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Project (Optional)</label>
                      <select
                        className="form-select"
                        value={formData.project}
                        onChange={(e) => setFormData({ ...formData, project: e.target.value })}
                      >
                        <option value="">Select Associated Project</option>
                        {projects.map(proj => (
                          <option key={proj.id} value={proj.id}>{proj.title}</option>
                        ))}
                      </select>
                    </div>
                    <div className="row g-3">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Submission Date *</label>
                        <input
                          type="date"
                          className="form-control"
                          value={formData.submissionDate}
                          onChange={(e) => setFormData({ ...formData, submissionDate: e.target.value })}
                          required
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Tender Value *</label>
                        <div className="input-group">
                          <span className="input-group-text">Rs.</span>
                          <input
                            type="text"
                            className="form-control"
                            value={formatDisplayValue(formData.value)}
                            onChange={(e) => handleAmountChange(e, 'value')}
                            required
                            placeholder="Enter tender value"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">{selectedTender ? 'Current Stage' : 'Initial Stage'}</label>
                      <select
                        className="form-select"
                        value={formData.currentStage}
                        onChange={(e) => {
                          const stageId = parseInt(e.target.value);
                          const stage = tenderStages.find(s => s.id === stageId);
                          setFormData({
                            ...formData,
                            currentStage: stageId,
                            stageName: stage.name
                          });
                        }}
                      // disabled={!!selectedTender} // Removed disable to allow editing
                      >
                        {tenderStages
                          .filter(stage => selectedTender ? true : stage.id === 1)
                          .map(stage => (
                            <option key={stage.id} value={stage.id}>{stage.name}</option>
                          ))}
                      </select>
                      {!selectedTender && <small className="text-muted">New tenders typically start as Draft. If Submitted, Earnest Money is required.</small>}
                    </div>
                    {formData.currentStage === 3 && (
                      <div className="mb-3">
                        <label className="form-label">Earnest Money Amount *</label>
                        <div className="input-group">
                          <span className="input-group-text">Rs.</span>
                          <input
                            type="text"
                            className="form-control"
                            value={formatDisplayValue(formData.earnestMoneyAmount)}
                            onChange={(e) => handleAmountChange(e, 'earnestMoneyAmount')}
                            required
                            placeholder="Enter earnest money amount"
                          />
                        </div>
                        <small className="text-muted">Required when submitting a tender</small>
                      </div>
                    )}

                    {/* Performance Security removed from creation/edit - only available via Awarded flow */}
                    <div className="mb-3">
                      <label className="form-label">Upload Documents</label>
                      <input
                        type="file"
                        className="form-control"
                        multiple
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                        onChange={handleFileChange}
                      />
                      <small className="text-muted">You can upload multiple files (PDF, DOC, XLS, Images)</small>
                      {uploadedFiles.length > 0 && (
                        <div className="mt-2">
                          <strong>Selected Files:</strong>
                          <ul className="list-group mt-2">
                            {uploadedFiles.map((file) => (
                              <li key={file.id} className="list-group-item">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <span>
                                    <i className="bi bi-file-earmark"></i> {file.name}
                                    <small className="text-muted ms-2">({(file.size / 1024).toFixed(2)} KB)</small>
                                  </span>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => removeFile(file.id)}
                                  >
                                    <i className="bi bi-x"></i>
                                  </button>
                                </div>
                                <div className="row g-2">
                                  <div className="col-md-6">
                                    <select
                                      className="form-select form-select-sm"
                                      value={file.docType}
                                      onChange={(e) => handleDocTypeChange(file.id, e.target.value)}
                                      required // Ideally should be required but harder to enforce on array here
                                    >
                                      <option value="">Select Document Type *</option>
                                      {documentTypes.map((type, idx) => (
                                        <option key={idx} value={type}>{type}</option>
                                      ))}
                                    </select>
                                  </div>
                                  {file.docType === 'Other' && (
                                    <div className="col-md-6">
                                      <input
                                        type="text"
                                        className="form-control form-select-sm"
                                        placeholder="Specify Document Type"
                                        value={file.customDocType}
                                        onChange={(e) => handleCustomDocTypeChange(file.id, e.target.value)}
                                      />
                                    </div>
                                  )}
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => { setShowModal(false); setSelectedTender(null); }}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary">
                      {selectedTender ? 'Update Tender' : 'Create Tender'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div >
        )
      }

      {
        showViewModal && selectedTender && (
          <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Tender Details - {selectedTender.tenderNo}</h5>
                  <button type="button" className="btn-close" onClick={() => {
                    setShowViewModal(false);
                    setSelectedTender(null);
                  }}><i className="bi bi-x-lg"></i></button>
                </div>
                <div className="modal-body">
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <strong>Tender Number:</strong>
                      <p>{selectedTender.tenderNo}</p>
                    </div>
                    <div className="col-md-6">
                      <strong>Organization:</strong>
                      <p>{selectedTender.organization}</p>
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <strong>Submission Date:</strong>
                      <p>{selectedTender.submissionDate}</p>
                    </div>
                    <div className="col-md-6">
                      <strong>Tender Value:</strong>
                      <p className="h5 text-primary">Rs. {selectedTender.value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</p>
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <strong>Current Stage:</strong>
                      <p>
                        <span className={`badge ${getStageBadge(selectedTender.currentStage)}`}>
                          {selectedTender.currentStage === 22 ? selectedTender.stageName : (tenderStages.find(s => s.id === selectedTender.currentStage)?.name || selectedTender.stageName)}
                        </span>
                      </p>
                    </div>
                    <div className="col-md-6">
                      <strong>Earnest Money:</strong>
                      <p>
                        {selectedTender.earnestMoneyAmount ? `Rs. ${selectedTender.earnestMoneyAmount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}` : 'N/A'}
                        {selectedTender.earnestMoneyReleased && <span className="badge bg-success ms-2">Released</span>}
                      </p>
                    </div>
                  </div>
                  <div className="mb-3">
                    <strong>Performance Security:</strong>
                    <p>
                      {selectedTender.performanceSecurityAmount ? `Rs. ${selectedTender.performanceSecurityAmount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}` : 'Not Submitted'}
                    </p>
                  </div>
                  {selectedTender.documents && selectedTender.documents.length > 0 && (
                    <div className="mb-3">
                      <strong>Documents:</strong>
                      <div className="list-group mt-2">
                        {selectedTender.documents.map((doc, index) => (
                          <div key={index} className="list-group-item d-flex justify-content-between align-items-center">
                            <span>
                              <i className="bi bi-file-earmark-text me-2"></i>
                              {doc.path ? (
                                <a
                                  href={doc.path}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ textDecoration: 'underline', color: '#0d6efd', cursor: 'pointer' }}
                                >
                                  {doc.name}
                                </a>
                              ) : (
                                <span>
                                  {doc.name}
                                  <span className="text-danger ms-2" style={{ fontSize: '0.8em' }}>(File not on server)</span>
                                </span>
                              )}
                              <small className="text-muted ms-2">({(doc.size / 1024).toFixed(2)} KB)</small>
                            </span>
                            <span className="badge bg-light text-dark">
                              {doc.docType === 'Other' ? (doc.customDocType || 'Other') : (doc.docType || doc.type)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* LINKED QUOTATIONS SECTION */}
                  <div className="mt-4 mb-4">
                    <h6 className="fw-bold border-bottom pb-2">Linked Quotations</h6>
                    {(!selectedTender.Quotations || selectedTender.Quotations.length === 0) ? (
                      <p className="text-muted fst-italic">No quotations linked to this tender.</p>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-sm table-bordered">
                          <thead className="table-light">
                            <tr>
                              <th>Quote No</th>
                              <th>Title</th>
                              <th>Status</th>
                              <th>Value</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedTender.Quotations.map(q => (
                              <tr key={q.id}>
                                <td>{q.quoteNo}</td>
                                <td>{q.title}</td>
                                <td>
                                  <span className={`badge ${tenderStages.find(s => s.id === q.currentStage)?.color ? 'bg-' + tenderStages.find(s => s.id === q.currentStage).color : 'bg-secondary'}`}>
                                    {q.stageName || q.status}
                                  </span>
                                </td>
                                <td className="text-end">
                                  {q.grandTotal ? `Rs. ${parseFloat(q.grandTotal).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}` : `Rs. ${parseFloat(q.value || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`}
                                </td>
                                <td>
                                  <button className="btn btn-sm btn-outline-info me-1" onClick={() => handleViewQuotation(q)} title="View Preview">
                                    <i className="bi bi-eye"></i>
                                  </button>
                                  <button className="btn btn-sm btn-outline-dark" onClick={async () => {
                                    try {
                                      const res = await quotationService.getById(q.id);
                                      generatePDF(res.data, 'standard');
                                    } catch (err) {
                                      Swal.fire('Error', 'Failed to fetch quotation for PDF.', 'error');
                                    }
                                  }} title="Download PDF">
                                    <i className="bi bi-file-pdf"></i>
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  <hr />
                  <div className="d-flex justify-content-end">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowViewModal(false)}
                    >
                      Close
                    </button>
                    {selectedTender.currentStage === 9 && (
                      <button
                        type="button"
                        className="btn btn-success ms-2"
                        onClick={() => {
                          setShowViewModal(false);
                          setShowPSModal(true);
                        }}
                      >
                        Submit Performance Security
                      </button>
                    )}
                    {selectedTender.currentStage === 21 && selectedTender.earnestMoneyAmount && !selectedTender.earnestMoneyReleased && (
                      <button
                        type="button"
                        className="btn btn-warning ms-2"
                        onClick={() => {
                          setShowViewModal(false);
                          setShowEMModal(true);
                        }}
                      >
                        Release Earnest Money
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Performance Security Submission Modal */}
      {
        showPSModal && selectedTender && (
          <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Submit Performance Security - {selectedTender.tenderNo}</h5>
                  <button type="button" className="btn-close" onClick={() => {
                    setShowPSModal(false);
                    setPsAmount('');
                    setSelectedTender(null);
                  }}><i className="bi bi-x-lg"></i></button>
                </div>
                <div className="modal-body">
                  <p><strong>Tender:</strong> {selectedTender.title}</p>
                  <p><strong>Current Stage:</strong> {selectedTender.stageName}</p>
                  <div className="mb-3">
                    <label className="form-label">Performance Security Amount *</label>
                    <div className="input-group">
                      <span className="input-group-text">Rs.</span>
                      <input
                        type="text"
                        className="form-control"
                        value={formatDisplayValue(psAmount)}
                        onChange={(e) => {
                          const val = e.target.value.replace(/,/g, '');
                          if (/^\d*\.?\d*$/.test(val)) setPsAmount(val);
                        }}
                        min="0"
                        step="0.01"
                        required
                        placeholder="Enter performance security amount"
                      />
                    </div>
                    <small className="text-muted">Once submitted, the project will be available for stock-out</small>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => {
                    setShowPSModal(false);
                    setPsAmount('');
                    setSelectedTender(null);
                  }}>
                    Cancel
                  </button>
                  <button type="button" className="btn btn-primary" onClick={handlePerformanceSecuritySubmit}>
                    Submit Performance Security
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Earnest Money Release Modal */}
      {
        showEMModal && selectedTender && (
          <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Release Earnest Money - {selectedTender.tenderNo}</h5>
                  <button type="button" className="btn-close" onClick={() => {
                    setShowEMModal(false);
                    setEmReleaseDate('');
                    setSelectedTender(null);
                  }}><i className="bi bi-x-lg"></i></button>
                </div>
                <div className="modal-body">
                  <p><strong>Tender:</strong> {selectedTender.title}</p>
                  <p><strong>Earnest Money Amount:</strong> Rs. {selectedTender.earnestMoneyAmount?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</p>
                  <div className="mb-3">
                    <label className="form-label">Release Date *</label>
                    <input
                      type="date"
                      className="form-control"
                      value={emReleaseDate}
                      onChange={(e) => setEmReleaseDate(e.target.value)}
                      required
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => {
                    setShowEMModal(false);
                    setEmReleaseDate('');
                    setSelectedTender(null);
                  }}>
                    Cancel
                  </button>
                  <button type="button" className="btn btn-primary" onClick={handleEarnestMoneyRelease}>
                    Release Money
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Organization Creation Modal */}
      {
        showOrgModal && (
          <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1080 }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">New Organization</h5>
                  <button type="button" className="btn-close" onClick={() => setShowOrgModal(false)}><i className="bi bi-x-lg"></i></button>
                </div>
                <div className="modal-body">
                  <div className="row g-3">
                    <div className="col-12 mb-3">
                      <label className="form-label">Organization Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={newOrgData.name}
                        onChange={(e) => setNewOrgData({ ...newOrgData, name: e.target.value })}
                        placeholder="Enter Organization Name"
                        autoFocus
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Abbreviation</label>
                      <input
                        type="text"
                        className="form-control"
                        value={newOrgData.abbreviation}
                        onChange={(e) => setNewOrgData({ ...newOrgData, abbreviation: e.target.value })}
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">NTN</label>
                      <input
                        type="text"
                        className="form-control"
                        value={newOrgData.ntn}
                        onChange={(e) => setNewOrgData({ ...newOrgData, ntn: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="row g-3">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">City</label>
                      <input type="text" className="form-control" value={newOrgData.city} onChange={(e) => setNewOrgData({ ...newOrgData, city: e.target.value })} />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Country</label>
                      <input type="text" className="form-control" value={newOrgData.country} onChange={(e) => setNewOrgData({ ...newOrgData, country: e.target.value })} />
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Address</label>
                    <textarea className="form-control" rows="2" value={newOrgData.address} onChange={(e) => setNewOrgData({ ...newOrgData, address: e.target.value })}></textarea>
                  </div>
                  <div className="row g-3">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Contact Person</label>
                      <input type="text" className="form-control" value={newOrgData.contactPerson} onChange={(e) => setNewOrgData({ ...newOrgData, contactPerson: e.target.value })} />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Designation</label>
                      <input type="text" className="form-control" value={newOrgData.designation} onChange={(e) => setNewOrgData({ ...newOrgData, designation: e.target.value })} />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Email</label>
                      <input type="email" className="form-control" value={newOrgData.email} onChange={(e) => setNewOrgData({ ...newOrgData, email: e.target.value })} />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Phone</label>
                      <input type="tel" className="form-control" value={newOrgData.phone} onChange={(e) => setNewOrgData({ ...newOrgData, phone: e.target.value })} />
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowOrgModal(false)}>Cancel</button>
                  <button type="button" className="btn btn-primary" onClick={handleCreateOrg}>Add Organization</button>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* View Quotation Preview Modal */}
      {
        showViewQuotationModal && selectedQuotationForView && createPortal(
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1070 }}>
            <div className="modal-dialog modal-xl modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Quotation Preview - {selectedQuotationForView.quoteNo}</h5>
                  <button type="button" className="btn-close" onClick={() => setShowViewQuotationModal(false)}><i className="bi bi-x-lg"></i></button>
                </div>
                <div className="modal-body p-4 bg-light">
                  <div className="card border-0 shadow-sm" id="quotation-preview">
                    <div className="card-body p-5" style={{ backgroundColor: 'white' }}>
                      {/* Header */}
                      <div className="d-flex justify-content-between align-items-center mb-5 border-bottom pb-4">
                        <div>
                          <h2 className="fw-bold text-primary mb-1">{company.name || 'Company Name'}</h2>
                          <div className="text-muted small">
                            {company.address}<br />
                            Phone: {company.phone} | Email: {company.email}
                          </div>
                        </div>
                        <div className="text-end">
                          <h4 className="fw-bold">QUOTATION</h4>
                          <span className={`badge ${tenderStages.find(s => s.id === selectedQuotationForView.currentStage)?.color ? 'bg-' + tenderStages.find(s => s.id === selectedQuotationForView.currentStage).color : 'bg-secondary'}`}>{selectedQuotationForView.stageName}</span>
                        </div>
                      </div>

                      {/* Info Grid */}
                      <div className="row mb-5">
                        <div className="col-md-6">
                          <h6 className="text-uppercase text-muted small fw-bold mb-3">Ref/Date</h6>
                          <table className="table table-sm table-borderless">
                            <tbody>
                              <tr>
                                <td className="text-muted py-1" style={{ width: '120px' }}>Quote No:</td>
                                <td className="fw-bold py-1">{selectedQuotationForView.quoteNo}</td>
                              </tr>
                              <tr>
                                <td className="text-muted py-1">Date:</td>
                                <td className="py-1">{selectedQuotationForView.submissionDate}</td>
                              </tr>
                              {selectedQuotationForView.validityDate && (
                                <tr>
                                  <td className="text-muted py-1">Valid Until:</td>
                                  <td className="py-1">{selectedQuotationForView.validityDate}</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                        <div className="col-md-6">
                          <h6 className="text-uppercase text-muted small fw-bold mb-3">Bill To</h6>
                          <div className="fw-bold fs-5 mb-1">{selectedQuotationForView.organization}</div>
                          <div className="mb-1">{selectedQuotationForView.toContact}</div>
                          <div className="text-muted">{selectedQuotationForView.designation}</div>
                        </div>
                      </div>

                      {/* Subject */}
                      <div className="mb-4">
                        <h6 className="text-muted small fw-bold text-uppercase mb-2">Subject</h6>
                        <div className="fs-5 fw-bold text-dark border-start border-4 border-primary ps-3 py-2 bg-light">
                          {selectedQuotationForView.subject || 'Quotation'}
                        </div>
                      </div>

                      {/* Items */}
                      <div className="table-responsive mb-5">
                        <table className="table table-hover align-middle">
                          <thead className="bg-dark text-white">
                            <tr>
                              <th className="py-3 ps-4">#</th>
                              <th className="py-3">Description</th>
                              <th className="py-3">Model</th>
                              <th className="py-3 text-center">Qty</th>
                              <th className="py-3 text-end">Unit Price</th>
                              <th className="py-3 text-end pe-4">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedQuotationForView.QuotationItems?.map((item, idx) => (
                              <tr key={idx}>
                                <td className="py-3 ps-4 text-muted">{idx + 1}</td>
                                <td className="py-3">
                                  <div className="fw-bold text-dark">{item.productName || item.description}</div>
                                  <div className="small text-muted">{item.description}</div>
                                </td>
                                <td className="py-3">{item.modelNumber || '-'}</td>
                                <td className="py-3 text-center">{item.quantity}</td>
                                <td className="py-3 text-end">Rs. {parseFloat(item.unitPrice).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</td>
                                <td className="py-3 text-end pe-4 fw-bold">Rs. {parseFloat(item.totalPrice).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="table-light">
                            <tr>
                              <td colSpan="5" className="text-end py-3 fw-bold">Sub-Total:</td>
                              <td className="text-end py-3 pe-4 fw-bold">Rs. {parseFloat(selectedQuotationForView.subTotal || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</td>
                            </tr>
                            {selectedQuotationForView.gstAmount > 0 && (
                              <tr>
                                <td colSpan="5" className="text-end py-2">GST ({selectedQuotationForView.gstRate}%):</td>
                                <td className="text-end py-2 pe-4">Rs. {parseFloat(selectedQuotationForView.gstAmount).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</td>
                              </tr>
                            )}
                            {selectedQuotationForView.installationCharges > 0 && (
                              <tr>
                                <td colSpan="5" className="text-end py-2">Installation/Commissioning:</td>
                                <td className="text-end py-2 pe-4">Rs. {parseFloat(selectedQuotationForView.installationCharges).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</td>
                              </tr>
                            )}
                            <tr className="table-primary">
                              <td colSpan="5" className="text-end py-3 fw-bold fs-5">Grand Total:</td>
                              <td className="text-end py-3 pe-4 fw-bold fs-5 text-primary">Rs. {parseFloat(selectedQuotationForView.grandTotal || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>

                      {/* Terms */}
                      <div className="row g-4 mb-5">
                        <div className="col-12">
                          <h6 className="text-uppercase text-muted small fw-bold mb-3 border-bottom pb-2">Terms & Conditions</h6>
                          <div className="bg-light p-4 rounded border-start border-4 border-secondary shadow-sm" style={{ whiteSpace: 'pre-wrap', fontSize: '0.95rem', lineHeight: '1.6' }}>
                            {selectedQuotationForView.termsAndConditions || 'Usual terms apply.'}
                          </div>
                        </div>
                      </div>

                    </div>
                  </div>
                </div>
                <div className="modal-footer bg-light border-top">
                  <div className="me-auto text-muted small">
                    Generated by RSMS on {new Date().toLocaleDateString()}
                  </div>
                  <button className="btn btn-outline-dark" onClick={() => generatePDF(selectedQuotationForView)}>
                    <i className="bi bi-file-pdf"></i> Download PDF
                  </button>
                  <button className="btn btn-primary px-4" onClick={() => setShowViewQuotationModal(false)}>Close Preview</button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )
      }
    </div>
  );
};

export default TenderManagement;
